export type TState = {
    [key: string]: any;
}

export type StateUpdater<T extends TState, TValue> = (payload: TValue, prevState: T) => Partial<T>;  

export type StateUpdaterMap<T extends TState, TActionMap> = {
    [action in keyof TActionMap]: StateUpdater<T, TActionMap[action]>;
}

export type StateObserver<T extends TState> = (state: Readonly<T>) => void;


/**
 * The public interface of the state so clients cannot mess with the actual StateManager
 */
export interface State<T extends TState, TActionMap> {
    /**
     * Subscribe to state changes
     * @param observer the observer function to be called when the state changes
     */
    subscribe: (observer: StateObserver<T>) => void;

    /**
     * Unsubscribe to state changes
     * @param observer the observer to be removed
     */
    unsubscribe: (observer: StateObserver<T>) => void;

    /**
     * executes an action and updates the state
     * @param action the action to execute
     * @param payload data associated or needed by the action to perform de update
     */
    execute: <TAction extends keyof TActionMap>(action: TAction, payload: TActionMap[TAction]) => void;

    /**
     * Get the current state
     * @returns the current state
     */
    get: () => Readonly<T>;
}

export class StateManager<T extends TState, TActionMap> {
    private _state: T;
    private _updaters: StateUpdaterMap<T, TActionMap>;
    private _observers: StateObserver<T>[] = [];
    private _stateFaçade: State<T, TActionMap>;
    private _scheduledNotification: any = null;
    private debug = false;

    constructor(initialState: T, updaters: StateUpdaterMap<T, TActionMap>, debug = false) {
        this._state = initialState;
        this._updaters = updaters;
        this._stateFaçade = { 
            subscribe: this.subscribe, 
            unsubscribe: this.unsubscribe, 
            execute: this.execute, 
            get: this.get 
        };
        this.debug = debug;
    }

    public subscribe = (observer: StateObserver<T>) =>
        this._observers.push(observer);

    public unsubscribe = (observer: StateObserver<T>) => this._observers = this._observers.filter(o => o !== observer);


    public execute = <TAction extends keyof TActionMap>(action: TAction, payload: TActionMap[TAction]) => {
        const updater = this._updaters[action];
        if (!updater) {
            throw new Error(`action [${action as string}] has no updater function`);
        }
        const state = updater(payload, this._state);
        this._state = { ...this._state, ...state };
        this.scheduleNotification();
    }

    public get = (): Readonly<T> => this._state;

    public get stateFaçade() {
        return this._stateFaçade;
    }

    private scheduleNotification() {
        if (this._scheduledNotification) {
            return;
        }
        this._scheduledNotification = setTimeout(() => this.notify(), 0);
    }

    private notify() {
        this._observers.forEach(o => o(this._state));
        this._scheduledNotification = null;
    }

}